import React, { useEffect, useState } from 'react';
import { client, consoleLogInDev } from 'shared';
import { useParams } from 'react-router';
import { Colors, Text } from 'library';
import EditableInfo from 'parent-portal/profile/BusinessProfile/EditableInfo';
import useAddress from 'parent-portal/profile/BusinessProfile/hooks/useAddress';
import { Grid } from '@mui/material';
import { IFAQQuestion } from 'parent-portal/business-profile/BusinessFaq/FaqAnswer';
import BusinessFaqSection from './BusinessFaqSection';
import LocationContactsSection from './LocationContactsSection';
import {
    BreakPreference,
    BusinessPreference,
    EditBusinessPreference,
} from 'parent-portal/profile/BusinessProfile/BusinessPreferences/models';
import BusinessPreferences from 'parent-portal/profile/BusinessProfile/BusinessPreferences';
import { useUserContext } from 'UserContext';
import { IBusinessLocation } from 'models';

export interface ILocationInfo {
    locationName: string;
}

export default function BusinessLocationPage() {
    const { id } = useParams<{ id: string }>();
    const { getDisplayText } = useUserContext();
    const [businessLocation, setBusinessLocation] = useState<IBusinessLocation>();
    const [mainLocationInfo, setMainLocationInfo] = useState<ILocationInfo>();
    const [programId, setProgramId] = useState('');
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const { addressInfo, resetAddress, submissionData: addressData, addressUpdated } = useAddress(
        businessLocation?.address,
    );
    const [faqQuestions, setFaqQuestions] = useState<IFAQQuestion[]>([]);
    const [businessPreference, setBusinessPreference] = useState<EditBusinessPreference>();
    const [breakPreferenceOptions, setBreakPreferenceOptions] = useState<BreakPreference[]>([]);

    function updateBusinesPreference(businessPreferences: BusinessPreference[]) {
        let businessPreference = businessPreferences.find((p) => p.business_location?.id?.toString() === id);
        businessPreference = businessPreference ?? businessPreferences.find((p) => p.business);
        if (businessPreference?.business) {
            setBusinessPreference({ ...businessPreference, business_location: parseInt(id), business: null, id: -1 });
        } else if (businessPreference) {
            setBusinessPreference({
                ...businessPreference,
                business_location: businessPreference.business_location?.id,
            });
        }
    }

    useEffect(refreshBusinessLocation, [id]);

    useEffect(() => {
        if (!editing && businessLocation) {
            setMainLocationInfo({ locationName: businessLocation.name });
            setProgramId(businessLocation.program_id);
            resetAddress(businessLocation.address);
        }
    }, [businessLocation, editing]);

    function refreshBusinessLocation() {
        client(`api/business-location/${id}/`)
            .then((res) => {
                setFaqQuestions(res.business_faqs);
                updateLocation(res.location);
                updateBusinesPreference(res.business_preferences);
                setBreakPreferenceOptions(res.break_preferences);
            })
            .catch(consoleLogInDev);
    }

    function saveLocation() {
        setLoading(true);
        client(`api/business-location/${id}/`, {
            method: 'PATCH',
            body: {
                name:
                    mainLocationInfo?.locationName === businessLocation?.name
                        ? undefined
                        : mainLocationInfo?.locationName,
                ...addressData,
                program_id: programId,
            },
        })
            .then((res) => {
                if (res.status === 500) {
                    consoleLogInDev(res.message);
                } else {
                    updateLocation(res);
                    setEditing(false);
                }
            })
            .finally(() => setLoading(false))
            .catch(consoleLogInDev);
    }

    function updateLocation(location?: IBusinessLocation) {
        if (location) {
            resetAddress(location.address);
            setBusinessLocation(location);
        }
    }

    function updateMainLocationInfo(key: keyof ILocationInfo, value: string) {
        if (mainLocationInfo) setMainLocationInfo({ ...mainLocationInfo, [key]: value });
    }

    if (!mainLocationInfo || !businessLocation) return <></>;

    const locationInfo = [
        {
            title: 'Location Name',
            onChange: (v: string) => updateMainLocationInfo('locationName', v),
            value: mainLocationInfo.locationName,
            error: !mainLocationInfo.locationName,
        },
        ...addressInfo,
        {
            title: 'Program Id',
            onChange: (v: string) => setProgramId(v),
            value: programId,
            error: false,
        },
    ];

    function saveDisabled() {
        if (addressUpdated()) {
            return false || loading;
        } else {
            return (
                loading ||
                (!(
                    mainLocationInfo &&
                    mainLocationInfo?.locationName !== businessLocation?.name &&
                    mainLocationInfo?.locationName?.length > 0
                ) &&
                    programId === businessLocation?.program_id)
            );
        }
    }

    function updateQuestion(question: IFAQQuestion) {
        let qIndex = faqQuestions.findIndex((q: IFAQQuestion) => q.id === question.id);
        let newQuestions = [...faqQuestions];
        if (qIndex !== -1) {
            newQuestions[qIndex] = question;
        } else {
            newQuestions.push(question);
        }
        setFaqQuestions(newQuestions);
    }

    const centerText = getDisplayText('center').capitalize();

    return (
        <Grid container direction="column" justifyContent="center" style={{ marginTop: 40, padding: '0px 20%' }}>
            <Text variant="display" textStyle={{ marginBottom: 10 }}>
                Edit {centerText} Location Information
            </Text>
            <Grid
                container
                item
                direction="column"
                style={{ padding: 20, backgroundColor: Colors.lightTurq, borderRadius: 18, gap: 20 }}
            >
                <EditableInfo
                    title={`${centerText} Information`}
                    editing={editing}
                    updateEditing={setEditing}
                    fields={locationInfo}
                    save={saveLocation}
                    saveDisabled={saveDisabled()}
                />
                <LocationContactsSection businessLocation={businessLocation} updateLocation={updateLocation} />
                {businessPreference ? (
                    <BusinessPreferences
                        businessPreference={businessPreference}
                        breakPreferenceOptions={breakPreferenceOptions}
                    />
                ) : null}

                <BusinessFaqSection
                    faqQuestions={faqQuestions}
                    updateQuestion={updateQuestion}
                    businessLocation={businessLocation}
                />
            </Grid>
        </Grid>
    );
}
